/* eslint-disable indent */
import {
  useState,
  useCallback,
  useContext,
  useEffect,
  ChangeEvent,
} from 'react';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import Modal from '@/components/Modal';
import { Select } from '@/components/Controls/Select';
import { damageLevelOptions, CORRECTION_TYPE } from '@/utils/constant.utils';
import {
  findElementParentDmg,
  findDmgInsideEl,
  getDmgValue,
  getDmgLabelByValue,
  initReport,
} from '@/utils/ia.utils';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { focusButton } from '@/utils/button.utils';
import { TFunction } from 'i18next';
type EditElementModalProps = {
  t?: TFunction<undefined>;
  id: string;
  equipmentClassId?: number;
  secondButtonRef?: React.RefObject<HTMLButtonElement>;
  onConfirm?: (newValue: string) => void;
  confirmLabel: string;
  defaultValue?: string;
  isElement: boolean;
};

const EditElementModal = ({
  t,
  id,
  equipmentClassId,
  secondButtonRef,
  onConfirm,
  confirmLabel,
  defaultValue,
  isElement,
}: EditElementModalProps) => {
  const {
    setReportEngineData,
    reportEngineData,
    dataAnnotation,
    dataAnnotationDamage,
  } = useContext(EngineCorrectionContext);
  const [valueTemp, setValueTemp] = useState<string>();
  const [dmgLevel, setDmgLevel] = useState<string>();
  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { uniqueDamagesSelectOptions, allDamagesSelectOptions } =
    useGetModerationDamages();

  useEffect(() => {
    if (valueTemp || dmgLevel) {
      focusButton(secondButtonRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTemp, dmgLevel]);

  useEffect(() => {
    if (isElement) {
      // For elements, initialize valueTemp with defaultValue
      if (defaultValue && defaultValue !== valueTemp) {
        setValueTemp(defaultValue);
      }
    } else {
      // For damages, extract the damage name and level from the label
      const dmgLabel = getDmgLabelByValue(
        defaultValue,
        allDamagesSelectOptions
      );
      const valueSplit = dmgLabel?.label.split('_');
      if (valueSplit && valueSplit[0] !== valueTemp) {
        setValueTemp(valueSplit[0]);
        setDmgLevel(valueSplit[1]);
      }
    }
  }, [defaultValue, isElement]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setValueTemp(e.target.value);
  };

  const handleLevelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setDmgLevel(e.target.value);
  };

  const onConfirmEdit = useCallback(() => {
    const { idReport } = reportEngineData;
    if (onConfirm) {
      let dmgValueAfter = '';
      if (isElement) {
        const elCurrent = dataAnnotation.find(
          (el) => el.elNumber == defaultValue
        );
        const dmgInside = findDmgInsideEl(dataAnnotationDamage, elCurrent);
        let linkObj = {};
        if (dmgInside.length > 0) {
          linkObj = {
            linkElDmgCorrectionType: CORRECTION_TYPE.MODIFICATION,
            linkElDmgBeforeCorrectionEl: defaultValue,
            linkElDmgAfterCorrectionEl: valueTemp,
            linkElDmgAfterCorrectionDmg: null,
            linkElDmgBeforeCorrectionDmg: null,
          };
        }

        setReportEngineData((prev) => ({
          ...prev,
          edit: [
            ...prev.edit,
            {
              ...initReport(idReport),
              isCorrectElements: false,
              elmtCorrectionTypes: CORRECTION_TYPE.MODIFICATION,
              elmtBeforeCorrection: defaultValue,
              elmtAfterCorrection: valueTemp,
              ...linkObj,
            },
          ],
        }));
      } else if (!isElement) {
        const dmgCurrent = dataAnnotationDamage.find(
          (dmg) => dmg.elNumber == defaultValue
        );
        const dmgCurrentLevel = getDmgLabelByValue(
          defaultValue,
          allDamagesSelectOptions
        ).label.split('_')[1];
        const parentEl = findElementParentDmg(dmgCurrent, dataAnnotation);
        dmgValueAfter = getDmgValue(
          valueTemp,
          dmgLevel,
          allDamagesSelectOptions
        );
        let linkObj = {};
        if (parentEl) {
          linkObj = {
            linkElDmgCorrectionType: CORRECTION_TYPE.MODIFICATION,
            linkElDmgBeforeCorrectionDmg: defaultValue,
            linkElDmgAfterCorrectionDmg: dmgValueAfter,
            linkElDmgAfterCorrectionEl: null,
            linkElDmgBeforeCorrectionEl: null,
          };
        }

        setReportEngineData((prev) => ({
          ...prev,
          edit: [
            ...prev.edit,
            {
              ...initReport(idReport),
              isCorrectDamages: false,
              dmgCorrectionTypes: CORRECTION_TYPE.MODIFICATION,
              dmgBeforeCorrection: defaultValue,
              dmgAfterCorrection: dmgValueAfter,
              dmgLevelBeforeCorrection: dmgCurrentLevel,
              dmgLevelAfterCorrection: dmgLevel,
              ...linkObj,
            },
          ],
        }));
      }

      onConfirm(isElement ? valueTemp : dmgValueAfter);
    }
  }, [isElement, valueTemp, defaultValue, dmgLevel]);

  return (
    <Modal
      id={id}
      title={isElement ? t('modifyEquipment') : t('modifyDamage')}
      size="small"
      centerY
      showSecondBtn
      secondButtonRef={secondButtonRef}
      onClickSecondBtn={onConfirmEdit}
      secondBtnLabel={confirmLabel}
    >
      <div className="grid gap-4">
        <div>
          <div className="text-2sm text-slate-500 font-medium mb-0.5">
            <span>
              {isElement ? t?.('common:equipment') : t?.('common:damage')}
            </span>
            <span className="ml-1 mt-0.5 text-red-FF2929 text-md">*</span>
          </div>
          <Select
            selectClassName="w-[319px] text-2sm"
            options={
              isElement
                ? equipmentClassId
                  ? equipmentsSelectOptions.filter(
                      (option) => option.value === equipmentClassId?.toString()
                    )
                  : equipmentsSelectOptions
                : uniqueDamagesSelectOptions
            }
            onChange={handleSelectChange}
            value={valueTemp || ''}
          />
        </div>
        {!isElement && (
          <div>
            <div className="text-2sm text-slate-500 font-medium mb-0.5">
              {t?.('common:severity')}
            </div>
            <Select
              selectClassName="text-2sm"
              options={damageLevelOptions}
              value={dmgLevel || '1'}
              onChange={handleLevelChange}
              defaultValue="1"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditElementModal;
